"use client";

import type React from "react";
import { Link } from "react-router-dom";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import { format } from "date-fns";
import { TableSkeleton } from "src/utils/TableSkeleton";
import FoodLoader from "src/components/Loader";

interface User {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_suspended: boolean;
  last_login?: string;
  created_at?: string;
  phone?: string;
}

interface UserTableProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  itemsPerPage: number;
  filteredData: User[];
  loading: boolean;
  onDownload: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const UserTable: React.FC<UserTableProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  filteredData,
  loading,
  onDownload,
  setCurrentPage,
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching Users"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  }

  return (
    <div className="bg-amber-50 rounded-xl shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-b border-amber-200">
        <h3 className="text-lg font-semibold text-amber-900 mb-3 sm:mb-0">
          User List
        </h3>
        <button
          onClick={onDownload}
          className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-md border border-amber-200 transition-colors"
        >
          Download CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 border-b border-amber-200 w-[60px]">
                S/N
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                First Name
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Last Name</th>
              <th className="px-5 py-3 border-b border-amber-200">Email</th>
              <th className="px-5 py-3 border-b border-amber-200">Phone</th>
              <th className="px-5 py-3 border-b border-amber-200">Status</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Last Login
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Registration Date
              </th>
              <th className="px-5 py-3 border-b border-amber-200 w-[80px]">
                View
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((user, index) => (
                <tr key={user._id} className="hover:bg-amber-50/80">
                  <td className="px-5 py-3 border-b border-amber-100">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {user.first_name}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {user.last_name}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                    {user.email}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                    {user.phone || "N/A"}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_suspended
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                        }`}
                    >
                      {user.is_suspended ? "Suspended" : "Active"}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 text-gray-500 whitespace-nowrap">
                    {user.last_login
                      ? format(new Date(user.last_login), "PPpp")
                      : "N/A"}{" "}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                    {user.created_at
                      ? format(new Date(user.created_at), "PPpp")
                      : "N/A"}{" "}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <Link to={`/pr-admin/users/user-details/${user._id}`}>
                      <button
                        className="p-1 rounded-full hover:bg-amber-100 transition-colors"
                        aria-label="View user details"
                      >
                        <img
                          src={removeRedEye || "/placeholder.svg"}
                          alt="View"
                          className="h-5 w-5"
                        />
                      </button>
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-10 text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="py-4 px-6 border-t border-amber-200">
        <div className="flex items-center justify-between">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-md border border-amber-200 ${currentPage === 1
                ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-900 hover:bg-amber-100"
              }`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={filteredData.length < itemsPerPage}
            className={`px-4 py-2 rounded-md border border-amber-200 ${filteredData.length < itemsPerPage
                ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-900 hover:bg-amber-100"
              }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserTable;
